import { useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError } from '../client';
import { apiOg } from './client';
const USERS = '/api/v1/admin/users';
const OPERATIONS = '/api/v1/admin/user-operations';
const operationRootKey = ['og', 'user-operations'];
const userKey = (userId) => ['og', 'user', userId];
const locationsKey = (userId) => [...userKey(userId), 'locations'];
const operationsKey = (userId, limit, offset) => [...userKey(userId), 'operations', limit, offset];
const operationKey = (operationId) => [...operationRootKey, operationId];
export function tariffPreviewKey(userId, input) {
    return [
        ...userKey(userId),
        'tariff-preview',
        input.options ?? null,
        input.deviceNumber ?? null,
    ];
}
export function paymentQuoteKey(userId, code, choice) {
    return [
        ...userKey(userId),
        'payment-quote',
        code,
        choice.presetId ?? null,
        choice.amount ?? null,
    ];
}
export function operationRetryDelay(failureCount, error) {
    const localBackoff = Math.min(10_000 * 2 ** failureCount, 40_000);
    if (error instanceof ApiError && error.status === 429 && error.retryAfterSeconds != null) {
        return Math.max(localBackoff, error.retryAfterSeconds * 1_000);
    }
    return localBackoff;
}
export function operationPollingInterval(operation, now = Date.now()) {
    if (!operation || operation.status !== 'processing')
        return false;
    if (operation.kind === 'user_deletion' || operation.kind === 'deletion')
        return false;
    const createdAt = Date.parse(operation.created_at);
    if (Number.isNaN(createdAt))
        return 4_000;
    return now - createdAt <= 30_000 ? 4_000 : 20_000;
}
function invalidateUserData(queryClient, userId) {
    void queryClient.invalidateQueries({
        queryKey: userId == null ? ['og', 'user'] : userKey(userId),
    });
}
export function useStartUserOperation(userId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (intent) => apiOg('users', `${USERS}/${userId}/${intent.path}`, {
            method: 'POST',
            headers: { 'Idempotency-Key': intent.idempotencyKey },
            body: intent.body,
        }),
        onSuccess: (result) => {
            queryClient.setQueryData(operationKey(result.operation_id), result);
            invalidateUserData(queryClient, userId);
            void queryClient.invalidateQueries({ queryKey: [...userKey(userId), 'operations'] });
        },
    });
}
export function useUserOperation(operationId, enabled = true) {
    const throttleUntil = useRef(0);
    return useQuery({
        queryKey: operationKey(operationId),
        queryFn: async () => {
            try {
                const result = await apiOg('users', `${OPERATIONS}/${encodeURIComponent(operationId)}`);
                throttleUntil.current = 0;
                return result;
            }
            catch (error) {
                if (error instanceof ApiError && error.status === 429 && error.retryAfterSeconds != null) {
                    throttleUntil.current = Math.max(throttleUntil.current, Date.now() + error.retryAfterSeconds * 1_000);
                }
                throw error;
            }
        },
        enabled: enabled && operationId !== '',
        refetchInterval: (query) => {
            const interval = operationPollingInterval(query.state.data);
            if (interval === false)
                return false;
            return Math.max(interval, throttleUntil.current - Date.now());
        },
        refetchIntervalInBackground: false,
        retry: (failureCount, error) => error instanceof ApiError && error.status === 429 && failureCount < 3,
        retryDelay: operationRetryDelay,
    });
}
export function useRetryUserOperation() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (operationId) => apiOg('users', `${OPERATIONS}/${encodeURIComponent(operationId)}/retry`, {
            method: 'POST',
        }),
        onSuccess: (result) => {
            queryClient.setQueryData(operationKey(result.operation_id), result);
            void queryClient.invalidateQueries({ queryKey: operationRootKey });
            invalidateUserData(queryClient);
        },
    });
}
export function useUserOperations(userId, limit = 50, offset = 0) {
    return useQuery({
        queryKey: operationsKey(userId, limit, offset),
        queryFn: () => apiOg('users', `${USERS}/${userId}/operations?limit=${limit}&offset=${offset}`),
        retry: false,
    });
}
export function useUpdateUserLocations(userId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (options) => apiOg('users', `${USERS}/${userId}/locations`, {
            method: 'PUT',
            body: { options },
        }),
        onSuccess: (catalog) => {
            queryClient.setQueryData(locationsKey(userId), catalog);
            invalidateUserData(queryClient, userId);
        },
        onError: (error) => {
            if (error instanceof ApiError && (error.status === 502 || error.status === 503)) {
                void queryClient.invalidateQueries({ queryKey: locationsKey(userId) });
                invalidateUserData(queryClient, userId);
            }
        },
    });
}
function tariffPreviewPath(userId, input) {
    const params = new URLSearchParams();
    for (const option of input.options ?? [])
        params.append('options', String(option));
    if (input.deviceNumber != null)
        params.set('device_number', String(input.deviceNumber));
    const query = params.toString();
    return `${USERS}/${userId}/tariff/preview${query ? `?${query}` : ''}`;
}
export function useTariffPreview(userId, input = {}, enabled = true) {
    return useQuery({
        queryKey: tariffPreviewKey(userId, input),
        queryFn: () => apiOg('users', tariffPreviewPath(userId, input)),
        enabled,
        retry: false,
    });
}
export function useUserPaymentSystems(userId) {
    return useQuery({
        queryKey: [...userKey(userId), 'payment-systems'],
        queryFn: () => apiOg('users', `${USERS}/${userId}/payment-systems`),
        retry: false,
    });
}
export function useUserPaymentOptions(userId, code) {
    return useQuery({
        queryKey: [...userKey(userId), 'payment-systems', code, 'options'],
        queryFn: () => apiOg('users', `${USERS}/${userId}/payment-systems/${encodeURIComponent(code)}/options`),
        enabled: code !== '',
        retry: false,
    });
}
function quotePath(userId, code, choice) {
    const params = new URLSearchParams({ payment_system: code });
    if (choice.presetId != null)
        params.set('preset_id', String(choice.presetId));
    else
        params.set('amount', String(choice.amount));
    return `${USERS}/${userId}/payment-quote?${params.toString()}`;
}
export function useUserPaymentQuote(userId, code, choice, enabled = true) {
    return useQuery({
        queryKey: paymentQuoteKey(userId, code, choice),
        queryFn: () => apiOg('users', quotePath(userId, code, choice)),
        enabled: enabled && code !== '',
        retry: false,
    });
}
export function useCreateUserPayment(userId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (body) => apiOg('users', `${USERS}/${userId}/payments`, {
            method: 'POST',
            body,
        }),
        retry: false,
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [...userKey(userId), 'operations'] });
        },
    });
}
export function useUserPayment(userId, orderId, enabled = true) {
    return useQuery({
        queryKey: [...userKey(userId), 'payment', orderId],
        queryFn: () => apiOg('users', `${USERS}/${userId}/payments/${encodeURIComponent(orderId)}`),
        enabled: enabled && orderId !== '',
        refetchInterval: (query) => (query.state.data?.state === 'pending' ? 4_000 : false),
        refetchIntervalInBackground: false,
        retry: false,
    });
}
